import styled from '@emotion/styled';
import {
  ButtonNormalSecondaryMediumCompact,
  TextDisplayAlt,
  TextParagraph,
  token,
} from '@ferreri/prever-component-lib';
import { LinkWrapper } from '@ferreri/prever-component-lib/nextjs';
import { useRouter } from 'next/router';
import { ServiceSection } from './ServicesSectionList';

type ServiceCardProps = {
  service: ServiceSection;
};

const getTagColor = (tagColor: string) => {
  switch (tagColor) {
    case 'pink':
      return '#FCE4EC';
    case 'gray':
      return '#E0E0E0';
    case 'green':
      return '#E8F5E9';
    case 'blue':
      return '#E3F2FD';
    default:
      return '#FCE4EC';
  }
};

const getTagTextColor = (tagColor: string) => {
  switch (tagColor) {
    case 'pink':
      return token.colors.solidSaudePrimaryPure;
    case 'gray':
      return token.colors.neutralXxDark;
    case 'green':
      return token.colors.solidPetPrimaryPure;
    case 'blue':
      return token.colors.solidClubePrimaryPure;
    default:
      return token.colors.solidSaudePrimaryPure;
  }
};

const RenderImageSection = ({
  imageSrc,
  service,
  isReversed,
}: {
  imageSrc: string;
  service: ServiceSection;
  isReversed: boolean;
}) => {
  return (
    <LinkWrapper href={service.buttonUrl}>
      <ImageSection isReversed={isReversed}>
        <ServiceImage src={imageSrc} alt={service.title} />
        <FeaturesContainer>
          {service.features.map((feature, index) => (
            <FeatureItem key={index}>
              <FeatureCircle>
                <FeatureIcon color={getTagTextColor(service.tagColor)}>
                  {feature.icon}
                </FeatureIcon>
              </FeatureCircle>
              <FeatureLabel>{feature.label}</FeatureLabel>
            </FeatureItem>
          ))}
        </FeaturesContainer>
      </ImageSection>
    </LinkWrapper>
  );
};

export const ServiceCard = ({ service }: ServiceCardProps) => {
  const router = useRouter();
  const imageSrc = `${router.basePath}/assets/img/${service.image}`;

  const isImageLeft = service.imagePosition === 'left';
  const isReversed = !isImageLeft;

  return (
    <ServiceCardContainer>
      <ServiceContent isReversed={isReversed}>
        {isImageLeft && (
          <RenderImageSection
            imageSrc={imageSrc}
            service={service}
            isReversed={isReversed}
          />
        )}

        <TextSection isReversed={isReversed}>
          <ServiceTag
            backgroundColor={getTagColor(service.tagColor)}
            textColor={getTagTextColor(service.tagColor)}
          >
            {service.tag}
          </ServiceTag>
          <ServiceTitle>{service.title}</ServiceTitle>
          <ServiceDescription>{service.description}</ServiceDescription>
          <ButtonWrapper>
            <LinkWrapper href={service.buttonUrl}>
              <ButtonNormalSecondaryMediumCompact>
                {service.buttonText}
              </ButtonNormalSecondaryMediumCompact>
            </LinkWrapper>
          </ButtonWrapper>
        </TextSection>

        {!isImageLeft && (
          <RenderImageSection
            imageSrc={imageSrc}
            service={service}
            isReversed={isReversed}
          />
        )}
      </ServiceContent>
    </ServiceCardContainer>
  );
};

const ServiceCardContainer = styled.div`
  width: 100%;
  padding: ${token.spacing.spacingXxLarge} ${token.spacing.spacingMedium};
`;

const ServiceContent = styled.div<{ isReversed: boolean }>`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr;
  gap: ${token.spacing.spacingLarge};
  align-items: stretch;

  @media (min-width: 968px) {
    grid-template-columns: 1fr 1fr;
    gap: ${token.spacing.spacingXxLarge};
    align-items: center;
  }
`;

const ImageSection = styled.div<{ isReversed: boolean }>`
  position: relative;
  padding-bottom: ${token.spacing.spacingXLarge};
  order: 2;

  @media (min-width: 968px) {
    order: ${({ isReversed }) => (isReversed ? 2 : 1)};
  }
`;

const ServiceImage = styled.img`
  width: 100%;
  max-width: 100%;
  height: auto;
  border-radius: ${token.borderRadius.borderRadiusMedium};
  object-fit: cover;

  @media (min-width: 768px) {
    max-height: 500px;
  }
`;

const FeaturesContainer = styled.div`
  position: absolute;
  bottom: -25px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: ${token.spacing.spacingXSmall};
  flex-wrap: nowrap;
  justify-content: center;
  width: calc(100% - ${token.spacing.spacingMedium} * 2);
  padding: 0 ${token.spacing.spacingMedium};
  box-sizing: border-box;

  @media (min-width: 768px) {
    gap: ${token.spacing.spacingSmall};
    width: calc(100% - ${token.spacing.spacingLarge} * 2);
    padding: 0 ${token.spacing.spacingLarge};
  }

  @media (min-width: 968px) {
    justify-content: flex-start;
  }
`;

const FeatureItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${token.spacing.spacingXSmall};
  flex: 1;
  min-width: 0;
  max-width: 100px;

  @media (min-width: 768px) {
    flex: 0 0 auto;
    min-width: 80px;
    max-width: none;
  }
`;

const FeatureCircle = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);

  @media (min-width: 768px) {
    width: 60px;
    height: 60px;
  }
`;

const FeatureIcon = styled.span<{ color: string }>`
  font-size: 20px;
  font-weight: 600;
  color: ${({ color }) => color};
  font-family: ${token.typography.fontFamily01};
  line-height: 1;

  @media (min-width: 768px) {
    font-size: 24px;
  }
`;

const FeatureLabel = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${token.colors.neutralXxDark};
  text-align: center;
  font-family: ${token.typography.fontFamily01};
  max-width: 80px;

  @media (min-width: 768px) {
    font-size: 14px;
    max-width: 100px;
  }
`;

const TextSection = styled.div<{ isReversed: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${token.spacing.spacingMedium};
  max-width: 100%;
  order: 1;

  @media (min-width: 968px) {
    max-width: 500px;
    order: ${({ isReversed }) => (isReversed ? 1 : 2)};
  }
`;

const ServiceTag = styled.span<{ backgroundColor: string; textColor: string }>`
  display: inline-block;
  padding: ${token.spacing.spacingXxSmall} ${token.spacing.spacingXSmall};
  background-color: ${({ backgroundColor }) => backgroundColor};
  color: ${({ textColor }) => textColor};
  border-radius: ${token.borderRadius.borderRadiusMedium};
  font-size: 12px;
  font-weight: 500;
  font-family: ${token.typography.fontFamily01};
  width: fit-content;
`;

const ServiceTitle = styled(TextDisplayAlt)`
  color: ${token.colors.neutralXxDark} !important;
  font-size: 20px;
  line-height: 1.2;

  @media (min-width: 768px) {
    font-size: 28px;
  }

  @media (min-width: 968px) {
    font-size: 32px;
  }
`;

const ServiceDescription = styled(TextParagraph)`
  color: ${token.colors.neutralXxDark} !important;
  font-size: 14px;
  line-height: 1.5;

  @media (min-width: 768px) {
    font-size: 16px;
  }
`;

const ButtonWrapper = styled.div`
  margin-top: ${token.spacing.spacingSmall};
`;
